import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Divider, FormControl, FormLabel,
    Input, Option, Select, Sheet, Stack, Switch, Typography,
} from '@mui/joy';

import { useAdmin } from '../../../AdminContext';
import { APIAdminAdsEconomics, APIAdminAdsEconomicsUpdate, adminAdsError } from '../../../api_admin_ads';

// Подписи для choice-значений, которые отдаёт сервер.
const CHOICE_LABELS = {
    city: 'Город',
    region: 'Регион',
    all_russia: 'Вся Россия',
    off: 'Выключено',
    warn: 'Предупреждать',
    block: 'Блокировать',
};

const choiceLabel = (v) => CHOICE_LABELS[v] || v;

export default function AdminAdsEconomicsPage() {
    const { adminToken: token, has } = useAdmin();
    const canEdit = has('ads.manage');

    const [groups, setGroups] = useState([]);
    const [values, setValues] = useState({});   // key -> текущее значение (строка)
    const [initial, setInitial] = useState({});  // key -> исходное значение
    const [defaults, setDefaults] = useState({}); // key -> значение по умолчанию

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsEconomics(token);
            const grs = res?.groups || [];
            const v = {}, def = {};
            grs.forEach((g) => g.items.forEach((it) => {
                v[it.key] = String(it.value ?? '');
                def[it.key] = String(it.default ?? '');
            }));
            setGroups(grs);
            setValues(v);
            setInitial(v);
            setDefaults(def);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить настройки'));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const changed = useMemo(() => {
        const out = {};
        Object.keys(values).forEach((k) => {
            if (values[k] !== initial[k]) out[k] = values[k];
        });
        return out;
    }, [values, initial]);

    const dirtyCount = Object.keys(changed).length;

    const setValue = (key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        setSuccess(null);
    };

    const save = async () => {
        if (!dirtyCount) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await APIAdminAdsEconomicsUpdate(token, changed);
            setInitial(values); // изменения приняты — новая база
            setSuccess(`Сохранено изменений: ${dirtyCount}`);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось сохранить'));
        } finally {
            setSaving(false);
        }
    };

    const renderField = (item) => {
        const value = values[item.key] ?? '';
        const isDefault = String(value) === String(defaults[item.key] ?? '');

        if (item.type === 'choice') {
            return (
                <Select value={value} disabled={!canEdit}
                        onChange={(_, v) => setValue(item.key, v)}>
                    {(item.options || []).map((opt) => (
                        <Option key={opt} value={opt}>{choiceLabel(opt)}</Option>
                    ))}
                </Select>
            );
        }
        if (item.type === 'bool') {
            return (
                <Switch checked={value === 'true'} disabled={!canEdit}
                        onChange={(e) => setValue(item.key, e.target.checked ? 'true' : 'false')} />
            );
        }
        // decimal | int
        return (
            <Input type="number" value={value} disabled={!canEdit}
                   endDecorator={!isDefault ? '●' : null}
                   onChange={(e) => setValue(item.key, e.target.value)} />
        );
    };

    if (loading) return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;

    return (
        <Box sx={{ maxWidth: 760 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography level="h4">Экономика рекламы</Typography>
                {canEdit && (
                    <Button loading={saving} disabled={!dirtyCount} onClick={save}>
                        Сохранить{dirtyCount ? ` (${dirtyCount})` : ''}
                    </Button>
                )}
            </Stack>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Изменения применяются сразу. Процент партнёра 0 = берётся общий процент.
            </Typography>

            {!canEdit && (
                <Alert color="neutral" sx={{ mb: 2 }}>
                    Просмотр без изменений: нужно право <b>ads.manage</b>.
                </Alert>
            )}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert color="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Stack spacing={2}>
                {groups.map((group) => (
                    <Sheet key={group.title} variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Typography level="title-md" sx={{ mb: 1.5 }}>{group.title}</Typography>
                        <Stack spacing={1.5}>
                            {group.items.map((item) => (
                                <FormControl key={item.key} orientation="horizontal"
                                             sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ pr: 2 }}>
                                        <FormLabel sx={{ mb: 0 }}>{item.label}</FormLabel>
                                        <Typography level="body-xs" color="neutral">
                                            по умолчанию: {defaults[item.key] || '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ minWidth: 200 }}>{renderField(item)}</Box>
                                </FormControl>
                            ))}
                        </Stack>
                    </Sheet>
                ))}
            </Stack>

            {canEdit && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" spacing={1.5}>
                        <Button loading={saving} disabled={!dirtyCount} onClick={save}>
                            Сохранить{dirtyCount ? ` (${dirtyCount})` : ''}
                        </Button>
                        <Button variant="outlined" color="neutral" disabled={!dirtyCount}
                                onClick={() => { setValues(initial); setSuccess(null); setError(null); }}>
                            Сбросить изменения
                        </Button>
                    </Stack>
                </>
            )}
        </Box>
    );
}